import {
  DestroyRef,
  Injectable,
  inject,
  signal
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

/** Breakpoint (px) desde el cual aplica la agenda desktop v2. */
export const AGENDA_DESKTOP_V2_MIN_WIDTH = 1024;

/** Estilos del p-dialog en v2: pantalla completa, un solo scroll (dentro de la grilla). */
export const AGENDA_DIALOG_STYLE_V2 = {
  width: '100vw',
  maxWidth: '100vw',
  height: '100vh',
  maxHeight: '100vh',
  borderRadius: '0'
};

export const AGENDA_DIALOG_CONTENT_STYLE_V2 = {
  display: 'flex',
  'flex-direction': 'column',
  flex: '1 1 auto',
  'min-height': '0',
  overflow: 'hidden',
  padding: '0 1rem 0.5rem'
};

/** Clave de localStorage con la preferencia del panel lateral. */
export const AGENDA_PANEL_STORAGE_KEY = 'girop.agenda.panelLateralAbierto';

/**
 * Estado compartido de la agenda desktop v2 (modales "Agenda del asesor" y
 * "Agenda del Consultor"): desktop (>= 1024 px) y panel lateral
 * colapsable con preferencia persistida.
 */
@Injectable({ providedIn: 'root' })
export class AgendaDesktopV2Service {
  private readonly document = inject(DOCUMENT);

  private readonly esDesktop = signal(false);

  /** true con viewport >= 1024 px. */
  readonly habilitado = this.esDesktop.asReadonly();

  readonly panelAbierto = signal(true);

  constructor() {
    this.panelAbierto.set(this.leerPreferencia());

    const mq = this.document.defaultView?.matchMedia?.(
      `(min-width: ${AGENDA_DESKTOP_V2_MIN_WIDTH}px)`
    );
    if (!mq) {
      return;
    }
    this.esDesktop.set(mq.matches);
    const onChange = (e: MediaQueryListEvent): void =>
      this.esDesktop.set(e.matches);
    mq.addEventListener('change', onChange);
    inject(DestroyRef).onDestroy(() =>
      mq.removeEventListener('change', onChange)
    );
  }

  togglePanel(): void {
    this.setPanelAbierto(!this.panelAbierto());
  }

  setPanelAbierto(abierto: boolean): void {
    this.panelAbierto.set(abierto);
    try {
      this.document.defaultView?.localStorage.setItem(
        AGENDA_PANEL_STORAGE_KEY,
        abierto ? '1' : '0'
      );
    } catch {
      // Storage bloqueado o lleno: la preferencia solo vive en memoria.
    }
  }

  private leerPreferencia(): boolean {
    try {
      const valor = this.document.defaultView?.localStorage.getItem(
        AGENDA_PANEL_STORAGE_KEY
      );
      return valor !== '0';
    } catch {
      return true;
    }
  }
}
